import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MathUtils, Vector3 } from 'three';

import { isValidNumber, roundToDecimals } from '../../../../../utils/math/number';
import { isDefined } from '../../../../../utils/variables';
import { PointId } from '../../../../../viewer/elements/design/design-node/utils/point-node';
import {
  AttributeData,
  AttributeId,
  SkyMapAttribute,
} from '../../../../../viewer/elements/design/skymap-extras';
import { SubscriptionTopic } from '../../../../../viewer/messaging/pubsub';
import { DesignTool } from '../../../../../viewer/tools/design-tool';
import { createPoint } from '../../../../../viewer/tools/utils/context-menu/design-tool/commands/point/create';
import {
  ClearAttribute,
  getTextAttributeOrCreate,
} from '../../../../../viewer/tools/utils/context-menu/design-tool/utils/attributes';
import { useSubscribe } from '../../../../hooks/use-subscribe';
import { ConfirmationDialog } from '../../../confirmation-dialog/confirmation-dialog';
import { LabelledContainer } from '../../../labelled-container/labelled-container';
import { Stack } from '../../../stack/stack';
import { TextBox } from '../../../text-box/text-box';

type Props = {
  tool: DesignTool;
};

const CreateDesignTextDialog = (props: Props) => {
  const { t } = useTranslation();
  const [showDialog, setShowDialog] = React.useState(false);
  const [textValue, setTextValue] = React.useState('Text');
  const [sizeValue, setSizeValue] = React.useState(0.5);
  const [rotationValue, setRotationValue] = React.useState(0);
  const [rotationInput, setRotationInput] = React.useState('0');
  const [textAttribute, setTextAttribute] = React.useState<SkyMapAttribute | undefined>(undefined);
  const [dialogData, setDialogData] = React.useState<{
    origin: Vector3;
    rotation: number;
    startPointId?: PointId;
  } | null>(null);

  useSubscribe(SubscriptionTopic.ShowCreateDesignTextDialog, (message) => {
    const pointNode = isDefined(message.startPointId)
      ? props.tool.asset.designNode.getPointById(message.startPointId)
      : undefined;

    const textAttribute = getTextAttributeOrCreate(props.tool);

    setTextAttribute(textAttribute.attribute);

    const existingAttributeData = pointNode?.attributes.get(textAttribute.attribute.id);
    const text =
      pointNode && existingAttributeData?.type === 'string'
        ? existingAttributeData.value
        : message.text;
    const rotation =
      pointNode &&
      existingAttributeData?.type === 'string' &&
      isDefined(existingAttributeData.rotation)
        ? existingAttributeData.rotation
        : message.rotation;
    const size =
      pointNode &&
      existingAttributeData?.type === 'string' &&
      isDefined(existingAttributeData.height)
        ? existingAttributeData.height
        : message.size;

    setTextValue(text);
    setSizeValue(roundToDecimals(size, 3));
    const rotationDegrees = roundToDecimals(MathUtils.radToDeg(rotation), 1);
    setRotationValue(rotationDegrees);
    setRotationInput(rotationDegrees.toString());

    setDialogData({
      origin: message.origin,
      rotation: message.rotation,
      startPointId: message.startPointId,
    });
    setShowDialog(true);
  });

  const handleConfirm = useCallback(async () => {
    if (!dialogData || !textAttribute) {
      return;
    }

    const attributes = new Map<AttributeId, AttributeData & ClearAttribute>();
    attributes.set(textAttribute.id, {
      type: 'string',
      value: textValue,
      height: sizeValue,
      rotation: MathUtils.degToRad(rotationValue),
      clearAttribute: textValue.trim().length === 0 ? true : undefined,
    });

    // Create the point with text attributes using the createPoint function
    props.tool.currentPoint = dialogData.origin;
    await createPoint(props.tool, {
      attributes,
      pointId: dialogData.startPointId,
      allowPointIntersection: true,
    }).action();

    setShowDialog(false);
    setDialogData(null);
  }, [dialogData, textAttribute, textValue, sizeValue, rotationValue, props.tool]);

  const handleClose = () => {
    setShowDialog(false);
    setDialogData(null);
  };

  if (!showDialog) {
    return null;
  }

  return (
    <ConfirmationDialog
      title={t('section.tools.textButton.text', { ns: 'skyviewDesign' })}
      onCancel={handleClose}
      onConfirm={handleConfirm}
    >
      <Stack direction="column" spacing={1}>
        <LabelledContainer text={t('section.tools.textButton.text', { ns: 'skyviewDesign' })}>
          {(formElementId) => (
            <TextBox
              autoFocus={true}
              id={formElementId()}
              maxLength={100}
              value={textValue}
              onChange={(e) => setTextValue(e.target.value)}
            />
          )}
        </LabelledContainer>

        <LabelledContainer text={t('textSize', { ns: 'skyviewDesign' })}>
          {(formElementId) => (
            <TextBox
              id={formElementId()}
              min={0.1}
              step={0.1}
              type="number"
              value={sizeValue.toString()}
              onChange={(e) => setSizeValue(MathUtils.clamp(parseFloat(e.target.value), 0.1, 10))}
            />
          )}
        </LabelledContainer>

        <LabelledContainer text={t('rotation', { ns: 'skyviewDesign' })}>
          {(formElementId) => (
            <TextBox
              id={formElementId()}
              max={360}
              min={-360}
              step={1}
              type="number"
              value={rotationInput}
              onBlur={() => {
                // On blur, ensure we have a valid number
                const numericValue = parseFloat(rotationInput);
                if (!isValidNumber(numericValue)) {
                  setRotationInput('0');
                  setRotationValue(0);
                } else {
                  const clampedValue = MathUtils.clamp(numericValue, -360, 360);
                  setRotationInput(clampedValue.toString());
                  setRotationValue(clampedValue);
                }
              }}
              onChange={(e) => {
                const inputValue = e.target.value;
                setRotationInput(inputValue);

                const numericValue = parseFloat(inputValue);
                if (isValidNumber(numericValue)) {
                  const clampedValue = MathUtils.clamp(numericValue, -360, 360);
                  setRotationValue(clampedValue);
                  // Update input to show clamped value if it was clamped
                  if (clampedValue !== numericValue) {
                    setRotationInput(clampedValue.toString());
                  }
                }
              }}
            />
          )}
        </LabelledContainer>
      </Stack>
    </ConfirmationDialog>
  );
};

export { CreateDesignTextDialog };
